import logger from '@/layers/support/monitoring/logger';
import { fetchWrapper } from '../utils/fetchWrapper';

const API_URL = '/api/programs/rerank';
const LOG_MODULE = 'FE [Presentation / Services / rerank]';

export interface RerankCandidate {
  id: number;
  title?: string;
  snippet: string;
  conditions?: string;
}

export interface RerankPatientParams {
  age?: number;
  gender?: string;
  additionalInfo?: string;
}

export interface RerankRequest {
  query: string;
  candidates: RerankCandidate[];
  patientParams?: RerankPatientParams;
}

export interface RerankScore {
  id: number;
  score: number;
}

interface RerankResponse {
  scores?: RerankScore[];
}

/**
 * Requests async LLM rerank scores for the given candidates.
 *
 * This is a best-effort enhancement layered on top of the instant DB-ordered
 * results: any failure (network error, non-OK response, malformed body) is
 * swallowed and reported as an empty score list, which the caller treats as
 * "keep DB order". The user never sees a rerank error.
 */
export const fetchRerankScores = async (request: RerankRequest): Promise<RerankScore[]> => {
  logger.debug(`${LOG_MODULE} fetchRerankScores: begin`, {
    query: request.query,
    candidateCount: request.candidates.length,
  });

  try {
    const result = await fetchWrapper.post<RerankResponse>(API_URL, request);
    const rawScores = Array.isArray(result?.scores) ? result.scores : [];

    // Normalize ids to numbers. The reranker schema allows the LLM to echo
    // candidate ids as strings; Program.id is numeric and mergeRerankedScores
    // matches by exact id, so a string id would silently fail to re-order.
    const scores: RerankScore[] = rawScores
      .map(s => ({ id: Number((s as { id: unknown }).id), score: s.score }))
      .filter(s => Number.isFinite(s.id) && typeof s.score === 'number');

    logger.debug(`${LOG_MODULE} fetchRerankScores: end`, { scoreCount: scores.length });
    return scores;
  } catch (error) {
    // Silent fallback: rerank is non-critical, the DB order already rendered.
    logger.warn(`${LOG_MODULE} fetchRerankScores: fallback to DB order`, {
      error: (error as Error)?.message,
    });
    return [];
  }
};
